import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCancelParentsBulkImportMutation } from '~entities/parent/api/cancel-parents-bulk-import';
import {
  type ParentsBulkImportJob,
  type ParentsBulkImportJobResult,
  useGetParentsBulkImportStatus,
} from '~entities/parent/api/get-parents-bulk-import-status';

import { BulkImportResultsPanel } from './bulk-import-results-panel';
import {
  getParentsBulkImportActiveIndex,
  isParentsBulkImportParentInFlight,
} from './bulk-import-progress-display';

import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Progress } from '~shared/shadcn/ui/progress';

interface ParentsBulkImportInitialData {
  totalRecords: number;
  estimatedTime?: string;
  correlationId?: string;
  deduplicated?: boolean;
}

interface ParentsBulkImportProgressModalProps {
  jobId: string | null;
  /** Synchronous API result (no jobId) — show summary immediately. */
  immediateResult?: ParentsBulkImportJobResult | null;
  /** True while POST /parents/bulk-import is in flight (upload + server work). */
  isRequestInFlight?: boolean;
  /** Multipart upload bytes progress (0–100), when available. */
  uploadProgress?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** @deprecated prefer initialData.totalRecords */
  initialTotalParents?: number;
  initialData?: ParentsBulkImportInitialData;
  onComplete?: () => void;
}

export const ParentsBulkImportProgressModal = ({
  jobId,
  immediateResult,
  isRequestInFlight = false,
  uploadProgress = null,
  open,
  onOpenChange,
  initialTotalParents,
  initialData,
  onComplete,
}: ParentsBulkImportProgressModalProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(
    null,
  );
  const [hasShownTerminalToast, setHasShownTerminalToast] = useState(false);
  const [stallDetected, setStallDetected] = useState(false);
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<{
    at: number;
    activeIndex: number;
    completed: number;
  } | null>(null);

  const isImmediateMode = immediateResult != null;
  const isAwaitingResponse =
    isRequestInFlight && !isImmediateMode && !jobId;

  const shouldPollStatus = open && !!jobId && !isImmediateMode;
  const {
    data: statusData,
    refetch: refetchStatus,
    isError: isStatusError,
    error: statusError,
  } = useGetParentsBulkImportStatus({
    jobId: jobId ?? '',
    refetchIntervalMs: shouldPollStatus ? 1000 : 0,
  });

  const cancelMutation = useCancelParentsBulkImportMutation();

  const polledJob = statusData?.job;

  const immediateJob: ParentsBulkImportJob | null = isImmediateMode
    ? {
        jobId: 'immediate',
        status: 'completed',
        totalRecords:
          initialData?.totalRecords ??
          initialTotalParents ??
          (immediateResult.success + immediateResult.failed),
        processedRecords:
          initialData?.totalRecords ??
          initialTotalParents ??
          (immediateResult.success + immediateResult.failed),
        currentParentEmail: null,
        progress: 100,
        result: immediateResult,
        errorMessage: null,
        completedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    : null;

  const job = isImmediateMode ? immediateJob : polledJob;
  const status = job?.status;
  const isProcessing =
    !isImmediateMode && (status === 'queued' || status === 'processing');
  const isCompleted = isImmediateMode || status === 'completed';
  const isFailed = !isImmediateMode && status === 'failed';
  const isCancelled = !isImmediateMode && status === 'cancelled';

  const showLiveProgress =
    open && !isImmediateMode && !isCompleted && !isFailed && !isCancelled;

  const displayJob = useMemo(() => {
    const total =
      job?.totalRecords ??
      initialData?.totalRecords ??
      initialTotalParents ??
      0;
    const processed = job?.processedRecords ?? 0;
    const activeIndex =
      job?.activeParentIndex ?? getParentsBulkImportActiveIndex(job);
    const progress =
      job?.progress ??
      (total > 0 ? Math.round((activeIndex / total) * 100) : 0);

    if (job) {
      return {
        ...job,
        totalRecords: total,
        processedRecords: processed,
        activeParentIndex: activeIndex,
        progress,
      };
    }

    if (!showLiveProgress) return null;

    return {
      jobId: jobId ?? 'pending',
      status: 'queued' as const,
      totalRecords: total,
      processedRecords: processed,
      activeParentIndex: processed,
      currentParentEmail: null,
      progress,
      result: null,
      errorMessage: null,
      completedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [
    job,
    jobId,
    initialData?.totalRecords,
    initialTotalParents,
    showLiveProgress,
  ]);

  const queuedSeemsStuck = useMemo(() => {
    if (!displayJob || displayJob.status !== 'queued') return false;
    if ((displayJob.processedRecords ?? 0) > 0) return false;
    if (isAwaitingResponse) return false;
    const created = displayJob.createdAt
      ? new Date(displayJob.createdAt).getTime()
      : 0;
    if (!created) return false;
    return Date.now() - created > 45_000;
  }, [displayJob, isAwaitingResponse]);

  const terminalCounts = useMemo(() => {
    const result = job?.result;
    return {
      succeeded: result?.success ?? 0,
      failed: result?.failed ?? 0,
      warnings: result?.importWarnings?.length ?? 0,
      attention: result?.duplicateUnresolved?.length ?? 0,
    };
  }, [job?.result]);

  useEffect(() => {
    if (!open) {
      setShowWarning(false);
      setProcessingStartTime(null);
      setHasShownTerminalToast(false);
      return;
    }

    const isActive = isAwaitingResponse || isProcessing;
    if (!isActive) {
      if (isCompleted || isFailed || isCancelled) {
        setProcessingStartTime(null);
      }
      return;
    }

    if (processingStartTime === null) {
      setProcessingStartTime(Date.now());
    }

    if (processingStartTime !== null) {
      const elapsed = Date.now() - processingStartTime;
      if (elapsed > 10000 && !showWarning) {
        setShowWarning(true);
      }
    }
  }, [
    open,
    isAwaitingResponse,
    isProcessing,
    isCompleted,
    isFailed,
    isCancelled,
    processingStartTime,
    showWarning,
  ]);

  useEffect(() => {
    if (!open || !isProcessing) {
      setStallDetected(false);
      setLastProgressSnapshot(null);
      return;
    }

    const activeIndex = getParentsBulkImportActiveIndex(displayJob);
    const completed = displayJob?.processedRecords ?? 0;
    const now = Date.now();

    if (lastProgressSnapshot == null) {
      setLastProgressSnapshot({ at: now, activeIndex, completed });
      return;
    }

    const progressed =
      activeIndex > lastProgressSnapshot.activeIndex ||
      completed > lastProgressSnapshot.completed;

    if (progressed) {
      setStallDetected(false);
      setLastProgressSnapshot({ at: now, activeIndex, completed });
      return;
    }

    if (now - lastProgressSnapshot.at >= 90000) {
      setStallDetected(true);
    }
  }, [open, isProcessing, displayJob, lastProgressSnapshot]);

  useEffect(() => {
    if (!open || !isImmediateMode || !immediateResult) return;
    if (hasShownTerminalToast) return;
    const { success, failed } = immediateResult;
    const warnings = immediateResult.importWarnings?.length ?? 0;
    const attention = immediateResult.duplicateUnresolved?.length ?? 0;
    const hasIssues = failed > 0 || warnings > 0 || attention > 0;
    if (success === 0 && hasIssues) {
      toast.warning(
        `Import finished with no new students. ${failed} failed, ${attention} need attention, ${warnings} warnings — see details below.`,
      );
    } else if (hasIssues) {
      toast.warning(
        `Import finished: ${success} succeeded, ${failed} failed, ${warnings} warnings, ${attention} need attention.`,
      );
    } else if (success > 0) {
      toast.success(`Import finished: ${success} student row(s) succeeded.`);
    }
    setHasShownTerminalToast(true);
  }, [open, isImmediateMode, immediateResult, hasShownTerminalToast]);

  useEffect(() => {
    if (!open || isImmediateMode) return;
    if (!jobId || !job) return;

    if (isCompleted || isFailed || isCancelled) {
      if (!hasShownTerminalToast) {
        if (isCompleted) {
          const { succeeded, failed, warnings, attention } = terminalCounts;
          const hasIssues = failed > 0 || warnings > 0 || attention > 0;
          if (succeeded === 0 && hasIssues) {
            toast.warning(
              `Import finished with no new students. ${failed} failed, ${attention} need attention, ${warnings} warnings — see details below.`,
            );
          } else if (hasIssues) {
            toast.warning(
              `Import finished: ${succeeded} succeeded, ${failed} failed, ${warnings} warnings, ${attention} need attention.`,
            );
          } else {
            toast.success(
              `Import finished: ${succeeded} student row(s) succeeded.`,
            );
          }
        } else if (isFailed) {
          toast.error(job.errorMessage ?? 'Import failed');
        } else if (isCancelled) {
          toast.message('Import cancelled');
        }
        setHasShownTerminalToast(true);
      }
    }
  }, [
    open,
    isImmediateMode,
    jobId,
    job,
    isProcessing,
    isCompleted,
    isFailed,
    isCancelled,
    hasShownTerminalToast,
    terminalCounts,
  ]);

  const handleOpenChange = (newOpen: boolean) => {
    if ((isProcessing || isAwaitingResponse) && !newOpen) {
      return;
    }
    if (!newOpen) {
      onOpenChange(false);
      if (isCompleted && onComplete) {
        onComplete();
      }
      return;
    }
    onOpenChange(newOpen);
  };

  const handleCancel = async () => {
    if (!jobId || !isProcessing) return;

    try {
      await cancelMutation.mutateAsync({ jobId });
      toast.success('Bulk import cancelled');
      void refetchStatus();
    } catch {
      toast.error('Failed to cancel bulk import');
    }
  };

  const getStatusText = () => {
    if (isAwaitingResponse) return 'Starting import…';
    if (!displayJob) return 'Initializing…';
    if (displayJob.status === 'queued') return 'Queued';
    if (displayJob.status === 'processing') {
      const active = getParentsBulkImportActiveIndex(displayJob);
      const total = displayJob.totalRecords;
      if (displayJob.currentParentEmail) {
        return `Parent ${active} of ${total}: ${displayJob.currentParentEmail}`;
      }
      return `Parent ${active} of ${total}`;
    }
    if (displayJob.status === 'completed') return 'Completed';
    if (displayJob.status === 'failed') return 'Failed';
    if (displayJob.status === 'cancelled') return 'Cancelled';
    return 'Unknown';
  };

  const progress = displayJob?.progress ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='max-h-[90vh] overflow-y-auto sm:max-w-[640px]'
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Bulk import parents & students</DialogTitle>
          {isCancelled ? (
            <DialogDescription>Import was cancelled</DialogDescription>
          ) : (
            <DialogDescription>
              {(() => {
                const n =
                  displayJob?.totalRecords ??
                  initialData?.totalRecords ??
                  initialTotalParents ??
                  null;
                const est = initialData?.estimatedTime;
                if (n != null) {
                  return (
                    <span>
                      Processing {n} parent group(s) from your file
                      {est ? ` · Estimated time: ${est}` : ''}
                    </span>
                  );
                }
                return 'Processing your CSV import';
              })()}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className='space-y-4'>
          {showLiveProgress && displayJob ? (
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Status</span>
                <div className='flex items-center gap-2'>
                  <Loader2 className='size-4 animate-spin' />
                  <span className='text-sm capitalize'>{getStatusText()}</span>
                </div>
              </div>

              <div className='space-y-2'>
                <div className='flex justify-between text-sm text-muted-foreground'>
                  <span>Progress</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className='h-2' />
                <div className='text-sm font-medium'>
                  Parent {getParentsBulkImportActiveIndex(displayJob)} of{' '}
                  {displayJob.totalRecords}
                </div>
                {isParentsBulkImportParentInFlight(displayJob) ? (
                  <div className='text-xs text-muted-foreground'>
                    {displayJob.processedRecords} completed — working on this
                    parent now
                    {displayJob.currentParentEmail
                      ? ` (${displayJob.currentParentEmail})`
                      : ''}
                  </div>
                ) : displayJob.processedRecords > 0 ? (
                  <div className='text-xs text-muted-foreground'>
                    {displayJob.processedRecords} parent group(s) finished
                  </div>
                ) : null}
                {stallDetected ? (
                  <Alert variant='destructive' className='mt-2'>
                    <AlertCircle className='size-4' />
                    <AlertDescription>
                      No progress for 90+ seconds. The import worker may be
                      stuck or still processing a slow parent — check the API
                      logs for ParentsBulkImport. You can cancel and retry with
                      a fresh upload.
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>

              {isAwaitingResponse &&
              uploadProgress != null &&
              uploadProgress < 100 ? (
                <div className='space-y-2 border-t pt-3'>
                  <div className='flex justify-between text-sm text-muted-foreground'>
                    <span>Uploading CSV</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className='h-2' />
                </div>
              ) : isAwaitingResponse ? (
                <p className='text-sm text-muted-foreground'>
                  Waiting for server to start the import job…
                </p>
              ) : null}
            </div>
          ) : null}

          {initialData?.deduplicated && isProcessing ? (
            <Alert>
              <AlertCircle className='size-4' />
              <AlertDescription>
                This file matches an import already in progress for your studio.
                Showing that job&apos;s status.
              </AlertDescription>
            </Alert>
          ) : null}

          {showWarning && (isAwaitingResponse || isProcessing) ? (
            <Alert>
              <AlertCircle className='size-4' />
              <AlertDescription>
                <div className='mb-1 font-semibold'>
                  This may take several minutes — please keep this dialog open
                </div>
                <div className='text-sm text-muted-foreground'>
                  If you refresh or close the page, the import continues in the
                  background, but you may lose this live progress view until you
                  poll status again.
                </div>
              </AlertDescription>
            </Alert>
          ) : null}

          {isStatusError && !isImmediateMode ? (
            <Alert variant='destructive'>
              <AlertCircle className='size-4' />
              <AlertDescription>
                Could not load import status
                {statusError instanceof Error ? `: ${statusError.message}` : ''}.
                The import may still be running on the server — try refreshing
                this page or uploading again.
              </AlertDescription>
            </Alert>
          ) : null}

          {queuedSeemsStuck && isProcessing ? (
            <Alert className='border-amber-200 bg-amber-50'>
              <AlertCircle className='size-4 text-amber-700' />
              <AlertDescription className='text-sm text-amber-950'>
                Still at 0% after a while usually means the background import
                worker is not running or the job was lost. Wait a bit longer, or
                close this dialog and upload again — stuck queued jobs are
                cleared automatically after a short timeout.
              </AlertDescription>
            </Alert>
          ) : null}

          {job && !showLiveProgress ? (
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Status</span>
                <div className='flex items-center gap-2'>
                  {isProcessing && <Loader2 className='size-4 animate-spin' />}
                  {isCompleted && (
                    <CheckCircle2 className='size-4 text-green-600' />
                  )}
                  {isFailed && <XCircle className='size-4 text-red-600' />}
                  {isCancelled && <XCircle className='size-4 text-amber-600' />}
                  <span className='text-sm capitalize'>{getStatusText()}</span>
                </div>
              </div>

              {isCompleted && !job.result ? (
                <Alert>
                  <AlertDescription className='text-sm text-muted-foreground'>
                    Import completed but no result details were returned. Try
                    closing and checking your parents/students list.
                  </AlertDescription>
                </Alert>
              ) : null}

              {isCompleted && job.result ? (
                <BulkImportResultsPanel job={job} showViewFullPageLink />
              ) : null}

              {isFailed && (
                <Alert variant='destructive'>
                  <XCircle className='size-4' />
                  <AlertDescription>
                    {job.errorMessage ?? 'Import failed with an unknown error'}
                  </AlertDescription>
                </Alert>
              )}

              {isCancelled && job.partialOutcome ? (
                <Alert className='border-amber-200 bg-amber-50'>
                  <AlertDescription className='text-sm text-amber-900'>
                    {job.partialOutcome === 'cancelled_heartbeat_timeout'
                      ? 'Stopped after inactivity: earlier rows may already be saved; later rows were not processed.'
                      : job.partialOutcome === 'cancelled_by_user'
                        ? 'You cancelled this import. Rows processed before cancel remain in your account.'
                        : 'Import was stopped before completion. Rows processed before cancel remain in your account.'}
                  </AlertDescription>
                </Alert>
              ) : isCancelled ? (
                <Alert className='flex items-center gap-2'>
                  <AlertDescription className='flex items-center gap-2'>
                    <XCircle className='size-5 text-amber-600' />
                    <span>Import was cancelled</span>
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : null}

          {!job && jobId && !isStatusError && !showLiveProgress ? (
            <div className='flex flex-col items-center justify-center gap-2 py-4'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
              <span className='text-sm text-muted-foreground'>
                Loading import status…
              </span>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          {isProcessing && (
            <Button
              variant='destructive'
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Cancelling...
                </>
              ) : (
                'Cancel import'
              )}
            </Button>
          )}
          {(isCompleted || isFailed || isCancelled) && (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
